import { useCallback, useEffect, useRef } from 'react';
import { NAVIGATION_KEYS } from 'src/interfaces/enums';
import { allowedKeyshortcutsPropagation } from './constants';

export type Item = {
  element: HTMLElement;
  index: number;
  startWithFocus?: boolean;
  keyActions?: {
    [value in NAVIGATION_KEYS]?: () => void;
  };
};

const useKeyboardInteractions = () => {
  const itemsRef = useRef<Item[]>([]);
  const focusedItemRef = useRef(0);

  const registerItem = useCallback((item: Omit<Item, 'element'>) => {
    return {
      ref: (element: HTMLElement | null) => {
        if (!element || itemsRef.current[item.index]) return;

        element.onfocus = () => (focusedItemRef.current = item.index);
        itemsRef.current[item.index] = { ...item, element };

        if (item.startWithFocus || item.index === 0) {
          focusedItemRef.current = item.index;
          element.focus();
        }
      },
    };
  }, []);

  function addFocusToNext() {
    if (focusedItemRef.current >= itemsRef.current.length - 1) return;
    focusedItemRef.current++;
    itemsRef.current[focusedItemRef.current].element.focus();
  }

  function addFocusToPrevious() {
    if (focusedItemRef.current <= 0) return;
    focusedItemRef.current--;
    itemsRef.current[focusedItemRef.current].element.focus();
  }

  const isKeyEventAllowedToPropagation = useCallback((key) => {
    return allowedKeyshortcutsPropagation.includes(key);
  }, []);

  useEffect(() => {
    const defaultActions: Item['keyActions'] = {
      [NAVIGATION_KEYS.ARROW_DOWN]: addFocusToNext,
      [NAVIGATION_KEYS.ARROW_UP]: addFocusToPrevious,
    };

    const callActionByKey = (key: NAVIGATION_KEYS) => {
      const action = itemsRef.current[focusedItemRef.current]?.keyActions?.[key];
      if (action) {
        action();
        return;
      }
      defaultActions[key]?.();
    };

    function onKeyDown(event: KeyboardEvent) {
      const { key } = event;

      if (key.includes('Arrow')) {
        event.preventDefault();
      }

      if (!isKeyEventAllowedToPropagation(key)) {
        event.stopPropagation();
      }

      callActionByKey(key as NAVIGATION_KEYS);
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [focusedItemRef, isKeyEventAllowedToPropagation, itemsRef]);

  return {
    registerItem,
  };
};
export default useKeyboardInteractions;
